import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../Register/Register.module.css';

import {Box, Typography, FormControl, FormControlLabel, Grid, Input, Select, MenuItem, Checkbox,} from '@mui/material';
import {useAuth} from '../../services/AuthProvider';
import {useNavigate} from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftSVG from '../../resources/chevron/blue-arrow-left.svg';
import GreenTickSVG from '../../resources/chevron/green-tick.svg';
import backgroundImage from "../../resources/Login/login-image.png";
import EditIconSVG from '../../resources/common/icon-pencil-square-filled.svg';
import {
  EmailPattern,
  getNewEthnicGroups,
  getPasswordRecommendations,
  getPasswordStrength,
  PasswordPattern, UsernamePattern
} from "../../services/Util";
import LoginRequiredPage from "../ErrorPages/LoginRequiredPage";
import {ethnicGroupOptions, genderGroupOptions, whoAreYouGroupOptions} from "../../services/Types";
import SpinnerModal from "../../components/Modal/Spinner";

enum ErrorEnum {
  INVALID_USERNAME = 'Please enter a username.',
  INVALID_USERNAME_FORMAT = 'The username can only contain alphanumeric characters (A-Z, a-z and 0-9).',
  USERNAME_IN_USE = 'The username <em>{PLACEHOLDER}</em> is already taken. Please select a different one.',
  INVALID_AGE_GROUP = "Please select a valid 'Who are you?' answer.",
  INVALID_ETHNIC_GROUP = 'Please tell us your main ethnic group.',
  INVALID_OTHER_ETHNIC = 'Please tell us your main ethnic group (Other).',
  INVALID_GENDER = 'Please select a gender.',
  INVALID_EMAIL_FORMAT = 'Please enter a valid email address.',
  EMAIL_IN_USE = 'The e-mail address <em>{PLACEHOLDER}</em> is already registered. Have you <a href="/forgot-password" style="color: #843534; font-weight: 500">forgotten your password</a>?',
  INVALID_PASSWORD = 'Password must be at least 8 characters.',
  INVALID_PASSWORD_FORMAT = 'Password must at have a number or symbol character.',
  INVALID_CONFIRM_PASSWORD = 'The specified passwords do not match.',
}

const initialErrorMessages = {
  username: '',
  ageGroup: '',
  ethnicGroup: '',
  ethnicGroupOther: '',
  gender: '',
  email: '',
  password: '',
  confirm_password: '',
}

const AccountSettings: React.FC = () => {
  const {isLoggedIn, userProfile, updateProfile, deleteAccount} = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [understand, setUnderstand] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState(initialErrorMessages);
  const [backendErrorMessages, setBackendErrorMessages] = useState('');
  const [informMessages, setInformMessages] = useState<string[]>([]);

  const [username, setUsername] = useState<string>('');
  const [ageGroup, setAgeGroup] = useState<string>('');
  const [ethnicGroups, setEthnicGroups] = useState<string[]>([]);
  const [ethnicGroupOther, setEthnicGroupOther] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  useEffect(() => {
    if (isLoggedIn && userProfile) {
      setUsername(userProfile.username ?? '');
      setAgeGroup(userProfile.ageGroup ?? '');
      setEthnicGroups(userProfile.ethnicGroups ?? []);
      setEthnicGroupOther(userProfile.ethnicGroupOther ?? '');
      setGender(userProfile.gender ?? '');
      setEmail(userProfile.email ?? '');
    }
  }, [isLoggedIn, userProfile]);

  const handleEthnicCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = getNewEthnicGroups(event, ethnicGroups);
    setEthnicGroups(newValues);
    if (newValues.includes('prefernottoanswer')) {
      setEthnicGroupOther("");
    }
  };

  // Password validation checks
  const passwordRecommendations = getPasswordRecommendations(password);
  const passwordStrength = getPasswordStrength(passwordRecommendations);

  // Return true if all conditions are met
  const checkFormValid = () : boolean => {
    const isUsernameValid = username.trim() !== '';
    const isUsernameFormatValid = UsernamePattern.test(username);
    const isAgeGroupValid = ageGroup.trim() !== '';
    const isEthnicGroupValid = ethnicGroups.length > 0;
    const isEthnicGroupOtherValid = !ethnicGroups.includes('other') || ethnicGroupOther.trim() !== '';
    const isGenderValid = gender.trim() !== '';
    const isEmailFormatValid = EmailPattern.test(email);
    const isPasswordValid = (password === '' && confirmPassword === '') || PasswordPattern.test(password);
    const isConfirmPasswordSame = password === confirmPassword;

    const errors = {
      username: !isUsernameValid ? ErrorEnum.INVALID_USERNAME : (!isUsernameFormatValid ? ErrorEnum.INVALID_USERNAME_FORMAT : ''),
      ageGroup: !isAgeGroupValid ? ErrorEnum.INVALID_AGE_GROUP : '',
      ethnicGroup: !isEthnicGroupValid ? ErrorEnum.INVALID_ETHNIC_GROUP : '',
      ethnicGroupOther: !isEthnicGroupOtherValid ? ErrorEnum.INVALID_OTHER_ETHNIC : '',
      gender: !isGenderValid ? ErrorEnum.INVALID_GENDER : '',
      email: !isEmailFormatValid ? ErrorEnum.INVALID_EMAIL_FORMAT : '',
      password: !isPasswordValid ? ErrorEnum.INVALID_PASSWORD_FORMAT : '',
      confirm_password: !isConfirmPasswordSame ? ErrorEnum.INVALID_CONFIRM_PASSWORD : '',
    };

    // Only update errorMessages state if the errors have changed
    if (JSON.stringify(errors) !== JSON.stringify(errorMessages)) {
      setErrorMessages(errors);
    }

    return (
      isUsernameValid && isUsernameFormatValid &&
      isAgeGroupValid &&
      isEthnicGroupValid && isEthnicGroupOtherValid &&
      isGenderValid &&
      isEmailFormatValid &&
      isPasswordValid && isConfirmPasswordSame
    );
  }

  const handleUpdate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();


    setInformMessages([]);
    setBackendErrorMessages("");

    if (!checkFormValid()) {
      setIsBannerOpen(true);
      scrollToElement('AccountSettingsBanner');
      return;
    }

    try {
      setIsSubmitting(true);
      await updateProfile({
        ...userProfile,
        username,
        ageGroup,
        ethnicGroups,
        ...(ethnicGroups.includes("other") && ethnicGroupOther && { ethnicGroupOther }),
        gender,
        email,
        ...(password && { password }),
      });
      // after successfully updated
      setIsEditing(false);
      setInformMessages(['Your account details have been updated.']);
      setIsBannerOpen(true);
      scrollToElement('AccountSettingsBanner');
    } catch (error: any) {
      // message for username and email exists error
      const errorMessage = error.message;
      const isUsernameError = errorMessage.includes('Username already exists');
      const isEmailError = errorMessage.includes('Email already exists');

      const formattedError = isUsernameError
        ? ErrorEnum.USERNAME_IN_USE.replace("{PLACEHOLDER}", username)
        : isEmailError
          ? ErrorEnum.EMAIL_IN_USE.replace("{PLACEHOLDER}", email)
          : errorMessage;

      setBackendErrorMessages(formattedError);
      setIsBannerOpen(true);

      scrollToElement('AccountSettingsBanner');
    } finally {
      setIsSubmitting(false);
    }
  }

  const scrollToElement = (elementId: string) => {
    const targetElement = document.getElementById(elementId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      targetElement.focus();
    } else {
      console.error('Element not found');
    }
  }

  const handleDelete = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setInformMessages([]);
    setBackendErrorMessages("");

    if (!understand) {
      setBackendErrorMessages("Please tick the checkbox to confirm deletion.");
      setIsBannerOpen(true);
      scrollToElement('AccountSettingsBanner');
      return;
    }

    try {
      setIsDeleting(true);
      await deleteAccount();
      navigate(`/account-deleted?email=${encodeURIComponent(email)}`);
    } catch (error: any) {
      setBackendErrorMessages(error.message);
      setIsBannerOpen(true);

      scrollToElement('AccountSettingsBanner');
    } finally {
      setIsDeleting(false);
    }
  }

  const errorMessagesArray = [
    backendErrorMessages,
    errorMessages.username,
    errorMessages.ageGroup,
    errorMessages.ethnicGroup,
    errorMessages.gender,
    errorMessages.email,
    errorMessages.password,
    errorMessages.confirm_password,
  ];

  return (!isLoggedIn ? <LoginRequiredPage /> :
      <div data-testid="account" style={{background: "#fff"}}>
        <SpinnerModal open={isDeleting} />

        <section id="AccountTitle" className={styles.RegistrationTitle}>
          <Typography variant="h1">My Account</Typography>
        </section>

        <span id="AccountSettingsBanner">
          {isBannerOpen && <div className={informMessages.length ? styles.RegisterBannerSuccess : styles.RegisterBanner}>
            <>
              {informMessages.map((message, index) => (
                <div style={{display: "flex", alignItems: "center"}}>
                  <img src={GreenTickSVG} alt="tick" style={{marginRight: 20}} />
                  <React.Fragment key={index}>
                    <div dangerouslySetInnerHTML={{__html: message}}/>
                  </React.Fragment>
                </div>
              ))}
              {errorMessagesArray.map((message, index) => (
                <React.Fragment key={index}>
                  <div dangerouslySetInnerHTML={{__html: message}}/>
                </React.Fragment>
              ))}
            </>
            <CloseIcon onClick={() => setIsBannerOpen(false)}/>
          </div>
          }
        </span>

        <Grid container>
          {/* Left Side - Form */}
          <Grid item xs={12} md={7} sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2
          }}>
            <Box component="form" noValidate onSubmit={handleUpdate}
                 sx={{width: '96%', maxWidth: 648,
                   '@media (min-width: 900px)': {
                     width: '80%'
                   }
                 }}>

              <Link to="/start-the-game" className={styles.GradientButton}>
                <img src={ChevronLeftSVG} alt="left" style={{marginRight: 8}} /> Back to SPARX game
              </Link>
              <br/>

              <div className="ShowFor768BigScreenOnly">
                <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
                  <Typography variant="h2" sx={{flex: '1', textAlign: 'left', color: '#333333', fontSize: 30, margin: 'auto', padding: '16px 0'}}>My Account</Typography>
                  {
                    !userProfile?.habitsId && <button style={{flex: '1'}}
                                                      className={`PrimaryButton ${styles.RegisterButton}`}
                                                      disabled={userProfile?.role === 'admin'}
                                                      onClick={(e) => {e.preventDefault(); setIsEditing(() => !isEditing); }}>
                      {isEditing ? "Editing..." : "Edit account details"}
                    </button>
                  }
                </div>
              </div>

              <div className="ShowFor768SmallScreenOnly">
                <div style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}} className="ShowFor768SmallScreenOnly">
                  <Typography variant="h2" sx={{flex: '1', textAlign: 'left', color: '#333', fontFamily: 'Figtree, sans-serif', fontSize: 22, margin: 'auto', padding: '16px 0'}}>My Account</Typography>
                  {
                    !userProfile?.habitsId && <button className={`${styles.EditButton}`}
                                                      disabled={userProfile?.role === 'admin'}
                                                      onClick={(e) => {e.preventDefault(); setIsEditing(() => !isEditing); }}>
                      <div style={{display: 'flex'}}>
                        <img src={EditIconSVG} alt="left" style={{marginRight: 8}} /> {isEditing ? "Editing..." : "Edit"}
                      </div>
                    </button>
                  }
                </div>
              </div>

              <br />

              {/* Username Field */}
              <Box sx={{ marginBottom: 5 }}>
                <Typography variant="subtitle2" gutterBottom>Username</Typography>
                <Input disabled fullWidth disableUnderline value={username}/>
              </Box>

              {/* Who are you Field */}
              <Box sx={{ marginBottom: 5 }}>
                <Typography variant="subtitle2" gutterBottom>Age range</Typography>
                <FormControl fullWidth>
                  <Select disabled={!isEditing} value={ageGroup} onChange={(e) => setAgeGroup(e.target.value as string)} displayEmpty inputProps={{ 'aria-label': 'WhoAreYou' }}>
                    <MenuItem value="" disabled>Please Select</MenuItem>
                    {whoAreYouGroupOptions.map((whoAreYouOption: any, index) => (
                      <MenuItem key={index} value={whoAreYouOption.value}>{whoAreYouOption.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Ethnic Groups Field */}
              <Box sx={{ marginBottom: 5, color: errorMessages.ethnicGroup ? 'error.main' : 'text.primary' }}>
                <Typography variant="subtitle2" color="inherit" gutterBottom>Ethnic group</Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 4}}>
                  {ethnicGroupOptions.map(group => (
                    <FormControlLabel key={group.value} label={group.label}
                                      control={<Checkbox disabled={!isEditing} checked={ethnicGroups.includes(group.value)} onChange={handleEthnicCheckboxChange} name={group.value}/>}
                    />
                  ))}
                </Box>
                {ethnicGroups.includes('other') && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: errorMessages.ethnicGroupOther ? 'error.main' : 'text.primary' }}>
                      If Other, which ethnic group?*
                    </Typography>
                    <Input disabled={!isEditing} autoFocus fullWidth disableUnderline placeholder=""
                           sx={{ borderColor: errorMessages.ethnicGroupOther ? 'error.main' : '#E6E8F3' }}
                           value={ethnicGroupOther} onChange={(e) => setEthnicGroupOther(e.target.value)}/>
                  </>
                )}
              </Box>

              {/* Gender Field */}
              <Box sx={{ marginBottom: 5 }}>
                <Typography variant="subtitle2" gutterBottom>Gender</Typography>
                <FormControl fullWidth>
                  <Select disabled={!isEditing} value={gender} onChange={(e) => setGender(e.target.value as string)} displayEmpty inputProps={{ 'aria-label': 'Gender' }}>
                    <MenuItem value="" disabled>Please Select</MenuItem>
                    {genderGroupOptions.map((genderOption: any, index) => (
                      <MenuItem key={index} value={genderOption.value}>{genderOption.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Email Field */}
              <Box sx={{ marginBottom: 5 }}>
                <Typography variant="subtitle2" gutterBottom>Email</Typography>
                <Input disabled autoFocus fullWidth disableUnderline placeholder="Type your email here..."
                       value={email} onChange={(e) => setEmail(e.target.value)}/>
              </Box>

              {/* Passwords */}
              {
                isEditing && <div>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 5,
                    '@media (min-width: 800px)': {
                      flexDirection: 'row',
                    }
                  }}>
                    {/* Password Field */}
                    <Box sx={{ flex: '1', color: (password.length !== 0 && errorMessages.password) ? 'error.main' : 'text.primary' }}>
                      <Typography variant="subtitle2" color="inherit" gutterBottom>Password*</Typography>
                      <Input type="password" fullWidth disableUnderline placeholder=""
                             sx={{ marginBottom: 1, borderColor: (password.length !== 0 && errorMessages.password) ? 'error.main' : '#E6E8F3' }}
                             value={password} onChange={(e) => setPassword(e.target.value)}/>
                      <Typography variant="caption" color="inherit">Password strength: {passwordStrength}</Typography>
                      {password && (passwordRecommendations.filter(rec => !rec.valid).length > 0) && (
                        <Box sx={{ marginTop: 0 }}>
                          <Typography variant="caption" color="inherit" gutterBottom>Recommendations to make your password stronger:</Typography>
                          <ul>
                            {passwordRecommendations.map((rec, index) => (
                              !rec.valid && <li key={index}>{rec.message}</li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    </Box>

                    <div style={{width: 'clamp(15px, 7.03vw - 41.2px, 60px)', height: '10px'}}></div>

                    {/* Confirm Password Field */}
                    <Box sx={{ flex: '1', color: errorMessages.confirm_password ? 'error.main' : 'text.primary' }}>
                      <Typography variant="subtitle2" color="inherit" gutterBottom>Confirm password*</Typography>
                      <Input type="password" fullWidth disableUnderline placeholder=""
                             sx={{ marginBottom: 1, borderColor: errorMessages.confirm_password ? 'error.main' : '#E6E8F3' }}
                             value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}/>
                      <Typography variant="caption" color="inherit">
                        Password match: { confirmPassword.length === 0 ? '' : (confirmPassword === password ? 'yes' : 'no') }
                      </Typography>
                    </Box>
                  </Box>

                  {/* submit button */}
                  <div>
                    <button className={`PrimaryButton ${styles.RegisterButton} WhiteButton`} onClick={(e) => {e.preventDefault(); setIsEditing(() => false); }}>Cancel</button>
                    <button className={`PrimaryButton ${styles.RegisterButton}`} disabled={isSubmitting || !checkFormValid()}>Save details</button>
                  </div>

                  {isSubmitting && <Typography variant="h2" style={{color: 'black'}}>Updating, please wait...</Typography>}
                </div>
              }
            </Box>

            {
              !isEditing && <Box component="form" noValidate onSubmit={handleDelete}
                                 sx={{width: '96%', maxWidth: 648, marginTop: '100px',
                                   '@media (min-width: 900px)': {
                                     width: '80%'
                                   }
                                 }}>
                <Typography variant="h2" sx={{textAlign: 'left', color: '#333333', fontSize: 30, margin: 'auto'}}>Delete my account</Typography>
                <Typography variant="body1" sx={{fontWeight: '500', color: '#333333'}}>
                  <br/>
                  <span>Deleting your account will delete all personal data associated with your account.</span>
                  <br/><br/>
                  <span>If you delete your account, this action cannot be undone. If you wish to restart SPARX, you will need to create a new account.</span>
                  <br/><br/>
                  {userProfile?.habitsId && <><span>Your SPARX account and access to the game will be removed, but you will still retain your HABITs account.</span><br/><br/></>}
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={understand} onChange={(e) => setUnderstand(e.target.checked)}/>}
                  label={"I understand, and want to delete my account"}
                />
                <br/><br/>

                {/* submit button */}
                <button className={`PrimaryButton ${styles.RegisterButton} RedButton`} disabled={isDeleting}>Delete account</button>

                {isDeleting && <Typography variant="h2" style={{color: 'black'}}>Deleting, please wait...</Typography>}
              </Box>
            }
          </Grid>

          {/* Right Side - Image */}
          <Grid item xs={false} md={5}
                sx={{
                  width: '40%',
                  height: 'auto',
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  backgroundPositionX: 'right'
                }}
          />
        </Grid>
      </div>
  );
};

export default AccountSettings;
